import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  X,
  FileText,
  Phone,
  Tag,
  RotateCcw,
} from "lucide-react";
import SearchableSelect from "./ui/SearchableSelect";
import ThemeDateInput from "./ui/ThemeDateInput";

function formatPhoneAccountLabel(account) {
  const name = account.phoneName || account.wabaName || "Account";
  const number = account.phoneNumber || "";
  return number ? `${name} · ${number}` : name;
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "#16A34A" },
  { value: "completed", label: "Completed", color: "#6b7280" },
  { value: "scheduled", label: "Scheduled", color: "#0064E0" },
  { value: "cancelled", label: "Cancelled", color: "#E41E3F" },
  { value: "aborted", label: "Aborted", color: "#B77400" },
  { value: "pending", label: "Pending", color: "#B77400" },
  { value: "paused", label: "Paused", color: "#7E57C2" },
];

const formatStatusOptionLabel = (option) => (
  <span className="flex items-center gap-2">
    <span
      className="w-2 h-2 rounded-full shrink-0"
      style={{ backgroundColor: option.color || "#9ca3af" }}
    />
    <span>{option.label}</span>
  </span>
);

export default function CampaignFilter({
  filters,
  onFilterChange,
  onApplyFilter,
  onClose,
  templates = [],
  phoneAccounts = [],
  loadingPhoneAccounts = false,
  loadingTemplates = false,
  onWhatsappAccountChange,
}) {
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "",
    campaign_name: filters.campaign_name || "",
    template_id: filters.template_id || "",
    whatsapp_account_id: filters.whatsapp_account_id || "",
    date_from: filters.date_from || "",
    date_to: filters.date_to || "",
  });

  useEffect(() => {
    setLocalFilters({
      status: filters.status || "",
      campaign_name: filters.campaign_name || "",
      template_id: filters.template_id || "",
      whatsapp_account_id: filters.whatsapp_account_id || "",
      date_from: filters.date_from || "",
      date_to: filters.date_to || "",
    });
  }, [filters]);

  const handleInputChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWhatsappAccountChange = (value) => {
    setLocalFilters((prev) => ({
      ...prev,
      whatsapp_account_id: value,
      template_id: "",
    }));
    onWhatsappAccountChange?.(value || null);
  };

  const handleApplyFilter = () => {
    onFilterChange(localFilters);
    onApplyFilter(localFilters);
    onClose?.();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      campaign_name: "",
      template_id: "",
      whatsapp_account_id: "",
      date_from: "",
      date_to: "",
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onApplyFilter(clearedFilters);
    onWhatsappAccountChange?.(null);
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== "" && value !== null
  );

  const activeFilterCount = Object.values(localFilters).filter(
    (v) => v !== "" && v !== null
  ).length;

  const phoneOptions = useMemo(
    () =>
      phoneAccounts.map((account) => ({
        value: String(account.phoneId),
        label: formatPhoneAccountLabel(account),
        searchText: `${account.phoneName || ""} ${account.phoneNumber || ""} ${account.wabaName || ""}`.toLowerCase(),
      })),
    [phoneAccounts]
  );

  const selectedPhoneOption = useMemo(
    () => phoneOptions.find((o) => o.value === String(localFilters.whatsapp_account_id)) || null,
    [phoneOptions, localFilters.whatsapp_account_id]
  );

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: String(template.id),
        label: template.name,
        searchText: template.name.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOption = useMemo(
    () => templateOptions.find((o) => o.value === String(localFilters.template_id)) || null,
    [templateOptions, localFilters.template_id]
  );

  const selectedStatusOption = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === localFilters.status) || null,
    [localFilters.status]
  );

  return (
    <aside className="ping-drawer camp-filter-drawer" aria-label="Filter campaigns">
      <div className="ping-drawer-head">
        <div>
          <h3>Filters</h3>
          <p>Narrow by name, status, template, account, or date</p>
        </div>
        <button type="button" className="camp-ico" onClick={onClose} aria-label="Close filters">
          <X size={16} />
        </button>
      </div>

      <div className="ping-drawer-body camp-filter-body">
        <label>
          Campaign name
          <div className="camp-filter-search">
            <Search size={14} />
            <input
              type="text"
              value={localFilters.campaign_name}
              onChange={(e) => handleInputChange("campaign_name", e.target.value)}
              placeholder="Search by name"
            />
            {localFilters.campaign_name && (
              <button type="button" onClick={() => handleInputChange("campaign_name", "")} aria-label="Clear name">
                <X size={13} />
              </button>
            )}
          </div>
        </label>

        <label>
          <span className="camp-filter-lab"><Tag size={12} /> Status</span>
          <SearchableSelect
            variant="campaign"
            options={STATUS_OPTIONS}
            value={selectedStatusOption}
            onChange={(opt) => handleInputChange("status", opt?.value || "")}
            placeholder="All statuses"
            isClearable
            formatOptionLabel={formatStatusOptionLabel}
            noOptionsMessage={() => "No statuses found"}
          />
        </label>

        <label>
          <span className="camp-filter-lab"><FileText size={12} /> Template</span>
          <SearchableSelect
            variant="campaign"
            options={templateOptions}
            value={selectedTemplateOption}
            onChange={(opt) => handleInputChange("template_id", opt?.value || "")}
            placeholder={loadingTemplates ? "Loading templates…" : "All templates"}
            isLoading={loadingTemplates}
            isDisabled={loadingTemplates}
            isClearable
            noOptionsMessage={() =>
              localFilters.whatsapp_account_id
                ? "No templates for this account"
                : "No templates found"
            }
          />
        </label>

        <label>
          <span className="camp-filter-lab"><Phone size={12} /> WhatsApp account</span>
          <SearchableSelect
            variant="campaign"
            options={phoneOptions}
            value={selectedPhoneOption}
            onChange={(opt) => handleWhatsappAccountChange(opt?.value || "")}
            placeholder={loadingPhoneAccounts ? "Loading accounts…" : "All accounts"}
            isLoading={loadingPhoneAccounts}
            isDisabled={loadingPhoneAccounts}
            isClearable
            noOptionsMessage={() => "No accounts found"}
          />
        </label>

        <div className="camp-filter-dates">
          <label>
            From
            <ThemeDateInput
              value={localFilters.date_from}
              onChange={(v) => handleInputChange("date_from", v)}
              placeholder="Start date"
              max={localFilters.date_to || undefined}
            />
          </label>
          <label>
            To
            <ThemeDateInput
              value={localFilters.date_to}
              onChange={(v) => handleInputChange("date_to", v)}
              placeholder="End date"
              min={localFilters.date_from || undefined}
            />
          </label>
        </div>
      </div>

      <div className="ping-drawer-foot camp-filter-foot">
        <span className="camp-filter-count">
          {hasActiveFilters ? `${activeFilterCount} active` : "No filters yet"}
        </span>
        {hasActiveFilters && (
          <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={handleClearFilters}>
            <RotateCcw size={13} />
            Reset
          </button>
        )}
        <button type="button" className="ping-btn ping-btn-primary ping-btn-sm" onClick={handleApplyFilter}>
          Apply
        </button>
      </div>
    </aside>
  );
}
